'use client';

import dayjs from 'dayjs';
import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import { EmailIntegrationService, type InkommandeEmail } from 'src/sections/email/EmailIntegrationService';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, formatRelativeReceived, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

export default function InkorgKort({ orgId }: KortProps) {
    const { data: email = [], isLoading } = useSWR<InkommandeEmail[]>(
        orgId ? ['inkommande-email', orgId] : null,
        () => EmailIntegrationService.hämtaMottagnaEmail(orgId),
        swrOptions
    );

    const senaste = [...email]
        .sort((a, b) => dayjs(b.mottagenDatum).valueOf() - dayjs(a.mottagenDatum).valueOf())
        .slice(0, 3);

    const antal = email.length;

    if (isLoading && antal === 0) {
        return (
            <DomainCard
                title="Inkorg"
                icon="solar:inbox-bold"
                theme={NyttFärgTema.Kombinationer[0]}
                href={paths.dashboard.inkorg}
                primaryMetric={{ value: '—', label: 'mail' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (antal === 0) {
        return (
            <DomainCard
                title="Inkorg"
                icon="solar:inbox-bold"
                theme={NyttFärgTema.Kombinationer[0]}
                href={paths.dashboard.inkorg}
                emptyMessage="Inga mottagna mail ännu. Anslut din email för att börja ta emot."
            />
        );
    }

    return (
        <DomainCard
            title="Inkorg"
            icon="solar:inbox-bold"
            theme={NyttFärgTema.Kombinationer[0]}
            href={paths.dashboard.inkorg}
            primaryMetric={{ value: antal, label: 'mail' }}
            statusChip={{ label: 'ANSLUTEN', color: ChipFärger[0] }}
        >
            <MiniList>
                {senaste.map((m) => (
                    <MiniRow
                        key={m.id}
                        primary={m.avsandare || 'Okänd avsandare'}
                        secondary={m.amne || '(Inget ämne)'}
                        meta={formatRelativeReceived(m.mottagenDatum)}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}
